import { useTranslation } from "react-i18next";
import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import NewsletterConfirmation from "../components/common/NewsletterConfirmation";
import Hero from "../components/home/Hero";
import DefinitionSection from "../components/home-v2/DefinitionSection";
import CapabilitiesSection from "../components/home-v2/CapabilitiesSection";
import ExperienceCollection from "../components/home/ExperienceCollection";
import Testimonial from "../components/home/Testimonial";
import Journey from "../components/home/Journey";
import InstagramFeed from "../components/home/InstagramFeed";
import CtaSection from "../components/home/CtaSection";
import VenueGrid from "../components/projects/VenueGrid";
import Pricing from "../components/home/Pricing";
import useAnalyticsContext from "../hooks/useAnalyticsContext.js";
import { trackCTA, trackLead } from "../lib/analytics";

/**
 * `/` and `/home` — the homepage.
 *
 * This page was reviewed at `/preview/home-v2` before it was promoted here on
 * 2026-09-06. The previous homepage is kept, unrouted, in `HomeLegacy.jsx`.
 *
 * Two sections are its own, and the rest are shared with nothing:
 *
 *   - `DefinitionSection`   — says plainly what Agapaé is, in prose
 *   - `CapabilitiesSection` — the five parts it brings together
 *
 * Hero, VenueGrid, ExperienceCollection, Testimonial, Journey, Pricing,
 * InstagramFeed and CtaSection are the components in `components/home/` that
 * the previous homepage also rendered, imported rather than copied.
 *
 * ── Crawlability and no-JS ────────────────────────────────────────────────
 * `index.html` is the static document this route is served from, and it
 * carries every heading and paragraph below as ordinary readable copy inside
 * `#root` — not a clipped screen-reader-only block, which is what it held
 * before this page was promoted. React replaces that subtree on mount.
 * `home.seo.test.mjs` fails if the document ever drifts from the locale files.
 *
 * The homepage is served from TWO such documents now: `index.html` for `/` and
 * `el/index.html` for `/el/`, the second derived from the first by
 * `scripts/build-home.mjs` with the copy taken from the Greek locale. This
 * component renders both — the same page at two URLs, not two pages — which is
 * why nothing below is language-specific except the head, and why the head is
 * derived rather than written out.
 *
 * `index.html` keeps its own head: GTM and the Consent Mode v2 default block
 * that must precede it. The canonical and robots values are declared here as
 * well, which is not a competing declaration: on a document load they restate
 * what `index.html` already says, and `/home` resolves to the same preferred
 * URL. They matter because `useDocumentMeta` restores whatever canonical was in
 * the head when a page mounted — so a route that declares none inherits the
 * previous one, which is exactly how /contact and the legal pages ended up
 * telling Google they were the homepage.
 *
 * ── On the "hv2" naming ───────────────────────────────────────────────────
 * `hv2-page`, `components/home-v2/` and the `homeV2` locale namespace are
 * historical: they are the names this page carried while it was a candidate.
 * They mean "the homepage", and were left alone rather than renamed across
 * ~40 CSS rules, two locale bundles and the guides index that reuses the same
 * list primitives. Nothing is versioned; there is no v1 in the codebase.
 */
const Home = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  // `/` and `/el/` are the same page in two languages, so the head is derived
  // from the page registry rather than written out: the canonical is
  // self-referencing (a Greek page canonicalised to English is a Greek page
  // asking not to be indexed), and the alternates are the reciprocal set the
  // two static documents already declare. `/home` renders this component too
  // and resolves to the same preferred URL, which is the behaviour it has had
  // all along.
  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.home.title",
    descriptionKey: "meta.home.description",
    canonical: canonicalUrlFor("/", language),
    alternates: alternatesFor("/"),
    robots: "index, follow",
  });

  // Re-initialises the Webflow interaction engine before the browser paints,
  // which is what keeps the sections below from flashing in un-animated.
  usePageShell();

  return (
    <div className="hv2-page">
      <Navbar />
      <CookieBanner />
      {/* Landing spot for the newsletter confirmation link — the API redirects
          here with ?newsletter=confirmed|expired|invalid. Mounted on Home
          because that's where LANDING_URL points; the catch-all route renders
          Home too, so a stray path still shows the message. */}
      <NewsletterConfirmation />
      <div className="html-embed w-embed">
        <style>{`.slide-nav { pointer-events: none; }`}</style>
      </div>
      <div className="main">
        <Hero />
        <DefinitionSection />
        <VenueGrid />
        <CapabilitiesSection />
        <Testimonial />
        <ExperienceCollection />
        <Journey />
        <Pricing />
        <InstagramFeed />
        <CtaSection
          text={t("home.cta.startJourney")}
          href={`${import.meta.env.VITE_APP_URL}/couple/create-event`}
          onClick={() => {
            trackCTA("home_final_cta", {
              ...analyticsContext,
              ctaName: "register",
            });
            trackLead("home_final_cta", {
              ...analyticsContext,
              ctaName: "register",
            });
          }}
        />
      </div>
      <Footer />
    </div>
  );
};

export default Home;
